import React from "react";

const formatDateTime = (value) => {
  if (!value) return "No activity yet";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatHours = (value) => `${Number(value || 0).toFixed(1)} hrs`;

function Panel({ title, description, children }) {
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      {children}
    </div>
  );
}

export default function AnalyticsInsightPanels({ analytics }) {
  return (
    <div className="grid gap-4 xl:grid-cols-3">
      <Panel
        title="Most Active Customer Users"
        description="Who is creating the most issues and generating the highest follow-up workload."
      >
        <div className="space-y-3">
          {analytics.activeCustomerUsers.length ? (
            analytics.activeCustomerUsers.map((item, index) => (
              <div key={`${item.customerUserId}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{item.customerUserName}</p>
                    <p className="mt-1 truncate text-sm text-slate-500">{item.customerName}</p>
                  </div>
                  <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                    {item.ticketCount} tickets
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1">{item.taskCount} tasks</span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1">
                    Last activity: {formatDateTime(item.lastActivityAt)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
              No customer user activity matched the current filters.
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title="Customer Issue Resolution Analytics"
        description="How each customer account is performing on open vs resolved issues."
      >
        <div className="space-y-3">
          {analytics.customerResolution.length ? (
            analytics.customerResolution.map((item, index) => (
              <div key={`${item.customerId}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{item.customerName}</p>
                    <p className="mt-1 truncate text-sm text-slate-500">{item.organizationName || "No organization"}</p>
                  </div>
                  <span className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
                    {formatPercent(item.resolutionRate)}
                  </span>
                </div>
                <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                  <span className="rounded-xl border border-slate-200 bg-white px-3 py-2">{item.ticketCount} total</span>
                  <span className="rounded-xl border border-slate-200 bg-white px-3 py-2">{item.openTicketCount} open</span>
                  <span className="rounded-xl border border-slate-200 bg-white px-3 py-2">{formatHours(item.averageResolutionHours)}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
              No customer resolution analytics are available for the current scope.
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title="Employee Assignment Ownership"
        description="Which employee is assigned to which customer workload, and how much activity each owner is carrying."
      >
        <div className="space-y-3">
          {analytics.employeeWorkload.length ? (
            analytics.employeeWorkload.map((item, index) => (
              <div key={`${item.employeeUserId}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{item.employeeName}</p>
                    <p className="mt-1 truncate text-sm text-slate-500">{item.employeeEmail || "No email added"}</p>
                  </div>
                  <span className="rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white">
                    {item.customerCount} customers
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-xl border border-slate-200 bg-white px-3 py-2">{item.ticketCount} tickets</span>
                  <span className="rounded-xl border border-slate-200 bg-white px-3 py-2">{item.taskCount} tasks</span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
              No employee ownership data matched the selected report filters.
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
